import {Box, Button, DropdownItem} from '@viptech/react-components'
import moment from 'moment'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import WebSocketStatus from 'src/common/components/websocket-status/WebsocketStatus'
import {AdvancedFilters, FilterSelectedFinal} from 'src/components/advancedFilters/AdvancedFilters'
import {TransparentButton} from 'src/components/advancedFilters/AdvancedFiltersBase'
import Dropdown from 'src/components/dropdown/Dropdown'
import {Input} from 'src/components/input/Input'
import useClients from 'src/hooks/api/client/useClients'
import useExportFacialDetections from 'src/hooks/api/facial/useExportFacialDetections'
import useFaceLists from 'src/hooks/api/facial/useFaceLists'
import {WebSocketStatusesEnum} from 'src/hooks/api/socket/useWebsocket'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import DeleteIcon from 'src/images/DeleteIcon'
import DownloadIcon from 'src/images/DownloadIcon'
import SearchIconInputSmallChangeColor from 'src/images/SearchIconInputSmall'
import {useCompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector-context'
import {ContainerFilters, FiltersSelectedContainer} from 'src/pages/alarms/AlarmsBase'
import {BoxResponsive, TitleOnButtonResponsive} from 'src/pages/lpr/LicensePlateBase'
import {Camera} from 'src/services/api'
import {fileDateFormat} from 'src/utilities/datetimeUtils'
import styled from 'styled-components'
import {FacesFilter} from '..'
import CustomCalendar from '../../../components/customCalendar/CustomCalendar'
import {FilterDate} from '../../alarms/AlarmsFunctions'
import {ContainerHeaderFacialRecognition} from './FacialRecognitionBase'

type Props = {
  cameras?: Array<Camera>
  search: string
  filter: FacesFilter
  filterDate: FilterDate
  chosenFilters: Array<FilterSelectedFinal>
  webSocketConnectionStatus: WebSocketStatusesEnum
  onPressBackToLive: () => void
  onPressDisconnect: () => void
  setChosenFilters: (filters: Array<FilterSelectedFinal>) => void
  onChangeFilter: (filter: FacesFilter) => void
  onSearch: (search: string) => void
  setFilterDate: (date: {from: string; to: string}) => void
  forceConnetionSocket: () => void
  isFilteringPastDate: () => boolean
}

export function FacialRecognitionHeader({
  cameras,
  filterDate,
  filter,
  chosenFilters,
  webSocketConnectionStatus,
  onPressBackToLive,
  setChosenFilters,
  setFilterDate,
  onChangeFilter,
  search,
  onSearch,
  onPressDisconnect,
  forceConnetionSocket,
  isFilteringPastDate,
}: Props) {
  const {selectedCompanies} = useCompaniesSelector()

  const {data: lists} = useFaceLists({companyIds: selectedCompanies.map((it) => it.id)})
  const {data: clients} = useClients({selectedCompanies: selectedCompanies.map((it) => it.id)})
  const {mutateAsync, isPending} = useExportFacialDetections()
  const {enqueueSnackbar} = useSnackbar()
  const {styledTheme} = usePersonalizedThemeByCompany()

  const [listOfCameras, setListOfCameras] = useState<DropdownItem[]>([])
  const [listOfClients, setListOfClients] = useState<DropdownItem[]>([])
  const [listOfFacesLists, setListOfFacesLists] = useState<DropdownItem[]>([])

  const [initialDatesToShowOnCalendar, setInitalDatesToShowOnCalendar] = useState<[Date, Date]>([new Date(filterDate.from!), new Date(filterDate.to!)])

  useEffect(() => {
    setInitalDatesToShowOnCalendar([new Date(filterDate.from!), new Date(filterDate.to!)])
  }, [filterDate])

  useEffect(() => {
    setListOfCameras(cameras ? cameras.map((item) => ({id: item.id.toString(), label: item.name})) : [])
    setListOfClients(clients?.data.data.entities?.map((item) => ({id: item.id.toString(), label: item.name})) ?? [])
    setListOfFacesLists(lists ? lists.data.data.entities.map((item) => ({id: item.id.toString(), label: item.name})) : [])
  }, [cameras, clients, lists])

  const filters = [
    {
      id: 'camera',
      label: 'Câmera',
      itemsOnMenu: listOfCameras,
    },
    {
      id: 'cliente',
      label: 'Cliente',
      itemsOnMenu: listOfClients,
    },
    {
      id: 'lista',
      label: 'Lista',
      itemsOnMenu: listOfFacesLists,
    },
    {
      id: 'desconhecidos',
      label: 'Faces desconhecidas',
    },
  ]

  const [showMoreFilters, setShowMoreFilters] = useState(false)

  const [disabledSelectFilters, setDisabledSelectFilters] = useState(false)

  const clearEmptyFilter = () => {
    const newFilters = chosenFilters.filter((it) => it.selectedItem)
    setChosenFilters(newFilters)
  }

  const handleSetFiltersSelected = (value: DropdownItem, index: number) => {
    setDisabledSelectFilters(false)

    const newFilter = [...chosenFilters]
    newFilter[index].selectedItem = value

    setChosenFilters(newFilter)

    if (newFilter[index].label === 'Câmera') {
      const valuesNotSelectedOnCameraFilter = listOfCameras.filter((it) => it.label !== value.label)
      setListOfCameras(valuesNotSelectedOnCameraFilter)

      onChangeFilter({...filter, cameraClientId: [Number(value.id), ...(filter.cameraClientId ?? [])]})
    } else if (newFilter[index].label === 'Lista') {
      const valuesNotSelectedOnListFilter = listOfFacesLists.filter((it) => it.label !== value.label)
      setListOfFacesLists(valuesNotSelectedOnListFilter)

      onChangeFilter({...filter, listName: [value.label, ...(filter.listName ?? [])]})
    } else if (newFilter[index].label === 'Cliente') {
      const valuesNotSelectedOnClientsFilter = listOfClients.filter((it) => it.label !== value.label)
      setListOfClients(valuesNotSelectedOnClientsFilter)

      onChangeFilter({...filter, clientIds: [Number(value.id), ...(filter.clientIds ?? [])]})
    } else if (newFilter[index].label === 'Faces desconhecidas') {
      onChangeFilter({...filter, faceName: null})
    }
  }

  const deleteFilter = (index: number) => {
    const deleteItem = chosenFilters[index].selectedItem
    if (!deleteItem) return

    if (chosenFilters[index].label === 'Câmera') {
      const selectedId = chosenFilters[index].selectedItem?.id
      if (!selectedId) return
      const newArrayOfCamerasFilter = filter.cameraClientId?.filter((it) => it !== +selectedId) ?? []

      setListOfCameras((prev) => [...prev, deleteItem])
      onChangeFilter({
        ...filter,
        cameraClientId: newArrayOfCamerasFilter.length === 0 ? undefined : newArrayOfCamerasFilter,
      })
    } else if (chosenFilters[index].label === 'Lista') {
      const selectedId = chosenFilters[index].selectedItem?.label
      if (!selectedId) return
      const newArrayOfClientsFilter = filter.listName?.filter((it) => it !== selectedId) ?? []
      setListOfFacesLists((prev) => [...prev, deleteItem])
      onChangeFilter({
        ...filter,
        listName: newArrayOfClientsFilter.length === 0 ? undefined : newArrayOfClientsFilter,
      })
    } else if (chosenFilters[index].label === 'Cliente') {
      const selectedId = chosenFilters[index].selectedItem?.id
      if (!selectedId) return
      const newArrayOfClientsFilter = filter.clientIds?.filter((it) => it !== +selectedId) ?? []
      setListOfClients((prev) => [...prev, deleteItem])
      onChangeFilter({
        ...filter,
        clientIds: newArrayOfClientsFilter.length === 0 ? undefined : newArrayOfClientsFilter,
      })
    }

    const deletedFilters = chosenFilters.filter((it) => it.selectedItem?.label !== deleteItem.label)
    setChosenFilters(deletedFilters)
    if (deletedFilters.length <= 0) {
      setShowMoreFilters(false)
      if (!isFilteringPastDate() && !search) forceConnetionSocket()
    }
  }

  const deleteAllFilters = () => {
    onChangeFilter({companyIds: selectedCompanies.map((it) => it.id)})
    setShowMoreFilters(false)
    setListOfCameras(cameras ? cameras.map((item) => ({id: item.id.toString(), label: item.name})) : [])
    setListOfClients(clients ? clients.data.data.entities.map((item) => ({id: item.id.toString(), label: item.name})) : [])
    setListOfFacesLists(lists ? lists.data.data.entities.map((item) => ({id: item.id.toString(), label: item.name})) : [])
    setChosenFilters([])
    setDisabledSelectFilters(false)
    onSearch('')
    if (!isFilteringPastDate() && !search) forceConnetionSocket()
  }

  const downloadReport = async () => {
    try {
      const start = filterDate.from ? new Date(filterDate.from) : undefined
      const end = filterDate.to ? new Date(filterDate.to) : undefined
      const startDate = start ? fileDateFormat(start.toISOString().replaceAll('/', '-')) : undefined
      const finalDate = end ? fileDateFormat(end.toISOString().replaceAll('/', '-')) : undefined
      const dateFileName = startDate && finalDate ? `_${startDate}_${finalDate}` : ''
      const {data} = await mutateAsync({
        start: start ? start.toISOString() : undefined,
        end: end ? end.toISOString() : undefined,
        fileType: 'csv',
        filter: {
          companyIds: selectedCompanies.map((it) => it.id),
          cameraIds: filter.cameraClientId,
          clientIds: filter.clientIds,
          listName: filter.listName,
        },
      })
      const blob = new Blob([data], {type: 'text/csv'})
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `relatório-detecções-faciais${dateFileName}.csv`
      a.click()
    } catch (error) {
      enqueueSnackbar(`Falha ao baixar relatório de detecções faciais, por favor tente novamente mais tarde`, {variant: 'error'})
    }
  }

  const handleOpenPartialFilters = (newFilter: FilterSelectedFinal) => {
    if (!listOfCameras.length && newFilter.id === 'camera') return
    if (!listOfClients.length && newFilter.id === 'cliente') return
    if (!listOfFacesLists.length && newFilter.id === 'lista') return
    if (chosenFilters.find((it) => it.id === 'desconhecidos' && newFilter.id === 'desconhecidos')) {
      enqueueSnackbar('O filtro escolhido já foi aplicado', {variant: 'info'})
      return
    }
    setDisabledSelectFilters(true)

    const moreFilters = [...chosenFilters, newFilter]
    setChosenFilters(moreFilters)
    setShowMoreFilters(true)
  }

  const handleChangeTimeStart = (dates: [Date, Date]) => {
    let from = moment(dates[0]).format()
    let to = moment(dates[1]).format()
    if (dates[0] === dates[1]) {
      from = moment(dates[0].setHours(0, 0, 0, 0)).format()
      to = moment(dates[1].setHours(23, 59, 59, 59)).format()
    }
    setFilterDate({
      from,
      to,
    })
    setInitalDatesToShowOnCalendar(dates)
    if (webSocketConnectionStatus === WebSocketStatusesEnum.DISCONNECTED && !chosenFilters.length && !search.length && !isFilteringPastDate())
      forceConnetionSocket()
  }

  useEffect(() => {
    if (!chosenFilters.length) {
      setShowMoreFilters(false)
      setDisabledSelectFilters(false)
    }
  }, [chosenFilters])

  return (
    <Box display='flex' justifyContent='space-between' width='100%' paddingRight='32px' direction='column'>
      <ContainerHeaderFacialRecognition>
        <BoxResponsive>
          <Input.Root
            hasIcon
            controlled
            maxLength={50}
            maxWidth='400px'
            minWidth='200px'
            width='100%'
            leftIcon={<SearchIconInputSmallChangeColor color={styledTheme.colors.primary} />}
            labelFontWeight='400'
            placeholder='Pesquisar'
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />

          <Dropdown
            height='40px'
            width='280px'
            fontSize='14px'
            labelFontSize='14px'
            borderColor={styledTheme.advancedFilter.borderColor}
            bgColor='white'
            iconColor={styledTheme.advancedFilter.iconColor}
            placeholder='Adicionar Filtro'
            items={filters}
            placeholderColor={styledTheme.advancedFilter.placeholderColor}
            onChangeSelected={(e) => handleOpenPartialFilters(e as FilterSelectedFinal)}
            disabled={disabledSelectFilters}
            dontReflectValueSelectedOnLabel
          />
        </BoxResponsive>

        <BoxWrapper>
          <WebSocketStatus status={webSocketConnectionStatus} onPressBackToLive={onPressBackToLive} onPressDisconnect={onPressDisconnect} />
          <Box display='flex' columnGap='12px'>
            <CustomCalendar initialDates={initialDatesToShowOnCalendar} setDate={(dates) => handleChangeTimeStart(dates)} hideRuler />
            <Button
              variant='outlined'
              height='40px'
              hoverColor='none'
              isLoading={isPending}
              loadingColor={styledTheme.colors.primary}
              disabled={isPending}
              onClick={downloadReport}>
              <Box display='flex' columnGap='8px' align='center'>
                <DownloadIcon />
                <TitleOnButtonResponsive>Baixar</TitleOnButtonResponsive>
              </Box>
            </Button>
          </Box>
        </BoxWrapper>
      </ContainerHeaderFacialRecognition>

      {showMoreFilters && (
        <Box align='center' display='flex' justifyContent='center' marginTop='12px'>
          <ContainerFilters>
            <FiltersSelectedContainer>
              {chosenFilters.map((it, index) => {
                return (
                  <AdvancedFilters
                    key={`${it}_${index}`}
                    clearEmptyFilter={clearEmptyFilter}
                    index={index}
                    filtersSelected={it}
                    setFiltersSelected={handleSetFiltersSelected}
                    deleteFilter={deleteFilter}
                  />
                )
              })}
            </FiltersSelectedContainer>

            <TransparentButton onClick={deleteAllFilters}>
              <Box display='flex' columnGap='10px' align='center'>
                <DeleteIcon />
                <p style={{textWrap: 'nowrap'}}>Limpar Seleção</p>
              </Box>
            </TransparentButton>
          </ContainerFilters>
        </Box>
      )}
    </Box>
  )
}

const BoxWrapper = styled.div`
  display: flex;

  @media (max-width: 767px) {
    justify-content: space-between;
    flex-direction: row-reverse;
  }
`
